import React from "react";
import {
  Box,
  Flex,
  Text,
  HStack,
  Link,
  Icon,
  Container,
  VStack,
  Heading,
  SimpleGrid,
  Divider,
  Button,
} from "@chakra-ui/react";
import { FaGithub, FaLinkedin, FaWhatsapp, FaArrowRight, FaTwitter, FaInstagram} from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";

const socials = [
  { icon: FaGithub, href: "https://github.com/TheIronA", label: "GitHub" },
  { icon: FaLinkedin, href: "https://www.linkedin.com/in/alwi-al-haddad/", label: "LinkedIn" },
  { icon: FaWhatsapp, href: "https://wa.me/601170171030", label: "WhatsApp" },
  { icon: FaTwitter, href: "https://twitter.com/TheIronA", label: "Twitter" },
  { icon: FaInstagram, href: "https://instagram.com/alwi.alhaddad", label: "Instagram" },
];

const Footer = () => {
  const handleScrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <Box
      as="footer"
      bg="#19191B"
      borderTop="1px"
      borderColor="whiteAlpha.200"
      py={{ base: 12, md: 16 }}
      position="relative"
    >
      {/* Decorative Element */}
      <Box
        position="absolute"
        top="-50px"
        left="50%"
        transform="translateX(-50%)"
        w="200px"
        h="100px"
        borderRadius="100%"
        bg="rgba(49, 130, 206, 0.08)"
        filter="blur(40px)"
        zIndex={0}
      />

      <Container maxW="1200px" position="relative" zIndex={1}>
        <SimpleGrid
          columns={{ base: 1, md: 3 }}
          spacing={{ base: 10, md: 6, lg: 8 }}
          mb={{ base: 10, md: 16 }}
        >
          {/* Column 1: Logo & Social Links */}
          <VStack align="flex-start" spacing={4}>
            <Box
              as={RouterLink}
              to="/"
              _hover={{ transform: "translateY(-2px)" }}
              transition="all 0.2s ease"
            >
              <Text
                fontSize="2xl"
                fontWeight="bold"
                letterSpacing="tight"
                color="brand.text.primary"
                position="relative"
                _after={{
                  content: "''",
                  position: "absolute",
                  left: "0",
                  bottom: "-2px",
                  width: "0",
                  height: "2px",
                  bg: "brand.secondary",
                  transition: "width 0.3s ease"
                }}
                _hover={{
                  color: "brand.secondary",
                  _after: {
                    width: "100%"
                  }
                }}
                aria-label="Alwi Al-haddad Logo"
                tabIndex={0}
              >
                AH.
              </Text>
            </Box>
            <HStack spacing={4} mt={2} wrap="wrap">
              {socials.map((social) => (
                <Link
                  key={social.label}
                  href={social.href}
                  isExternal
                  color="brand.text.primary"
                  _hover={{
                    color: "brand.accent",
                    transform: "translateY(-2px)",
                  }}
                  transition="all 0.2s ease"
                  aria-label={social.label}
                >
                  <Icon as={social.icon} w={5} h={5} />
                </Link>
              ))}
            </HStack>
          </VStack>

          {/* Column 2: Navigation */}
          <VStack align="flex-start" spacing={4}>
            <Heading as="h3" fontSize="md" color="brand.text.muted" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
              Navigation
            </Heading>
            <VStack align="flex-start" spacing={2}>
              <Link
                as={RouterLink}
                to="/"
                color="brand.text.secondary"
                fontSize="md"
                _hover={{ color: "brand.secondary", ml: 1 }}
                transition="all 0.2s ease"
              >
                Home
              </Link>
              <Link
                as={RouterLink}
                to="/projects"
                color="brand.text.secondary"
                fontSize="md"
                _hover={{ color: "brand.secondary", ml: 1 }}
                transition="all 0.2s ease"
              >
                Projects
              </Link>
              <Link
                as={RouterLink}
                to="/blog"
                color="brand.text.secondary"
                fontSize="md"
                _hover={{ color: "brand.secondary", ml: 1 }}
                transition="all 0.2s ease"
              >
                Blog
              </Link>
              <Link
                as="a"
                href="#contactme-section"
                color="brand.text.secondary"
                fontSize="md"
                _hover={{ color: "brand.secondary", ml: 1 }}
                transition="all 0.2s ease"
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('contactme-section');
                  if (element) {
                    const offset = 80;
                    const bodyRect = document.body.getBoundingClientRect().top;
                    const elementRect = element.getBoundingClientRect().top;
                    const elementPosition = elementRect - bodyRect;
                    const offsetPosition = elementPosition - offset;

                    window.scrollTo({
                      top: offsetPosition,
                      behavior: "smooth",
                    });
                  }
                }}
              >
                Contact
              </Link>
            </VStack>
          </VStack>

          {/* Column 3: Contact */}
          <VStack align="flex-start" spacing={4}>
            <Heading as="h3" fontSize="md" color="brand.text.muted" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
              Get in Touch
            </Heading>
            <Text color="brand.text.secondary" fontSize="md">
              Interested in working together? Feel free to reach out.
            </Text>
            <Button
              as="a"
              href="#contactme-section"
              rightIcon={<FaArrowRight />}
              colorScheme="blue"
              size="md"
              onClick={(e) => {
                e.preventDefault();
                const element = document.getElementById('contactme-section');
                if (element) {
                  const offset = 80;
                  const bodyRect = document.body.getBoundingClientRect().top;
                  const elementRect = element.getBoundingClientRect().top;
                  const elementPosition = elementRect - bodyRect;
                  const offsetPosition = elementPosition - offset;

                  window.scrollTo({
                    top: offsetPosition,
                    behavior: "smooth",
                  });
                }
              }}
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "md",
              }}
            >
              Contact Me
            </Button>
          </VStack>
        </SimpleGrid>

        <Divider borderColor="whiteAlpha.300" />

        <Flex
          mt={6}
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align="center"
          gap={4}
        >
          <Text color="brand.text.muted" fontSize="sm">
            © {new Date().getFullYear()} Alwi Al-haddad. All rights reserved.
          </Text>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleScrollToTop}
            color="brand.text.muted"
            _hover={{
              bg: "whiteAlpha.100",
              color: "brand.text.primary",
            }}
          >
            Back to Top
          </Button>
        </Flex>
      </Container>
    </Box>
  );
};

export default Footer;
